import { Box, IconButton, Link, Snackbar } from "@mui/material";
import CloseIcon from "assets/CloseIcon";
import DeleteCircle from "assets/DeleteCircle";
import { ReactComponent as CheckCircle } from "assets/validation-check-circle.svg";
import { ReactComponent as WarningCircle } from "assets/warning-circle.svg";
import ThemeContext from "components/app/Context";
import { useContext, useEffect, useState } from "react";
import { clearFormState } from "redux/forms.slice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { FEEDBACK_TIMER } from "utils/CommonConstants";
import { responseStatusTypes } from "utils/Types";
import Typography from "./Typography";

interface Props {
  form: string;
  message?: string;
  linkLabel?: string;
  href?: string;
  actionStatus?: any;
  onCloseChange?: () => void;
  clickHandler?: () => void;
  isCrossHidden?: boolean;
}

function Feedback(props: Props) {
  const dispatch = useAppDispatch();
  const { form, message, linkLabel, href, actionStatus, onCloseChange, clickHandler, isCrossHidden = false } = props;
  let status = useAppSelector((state) => state.formDetails?.[form]?.status);
  const details = useAppSelector((state) => state.formDetails?.[form]?.details);
  const [showFeedback, setShowFeedback] = useState(false);
  const [styling, setStyling] = useState<any>();
  const { isMobileScreen } = useContext(ThemeContext);

  useEffect(() => {
    const getStyling = (variant: any) => {
      switch (variant) {
        case "error": {
          setStyling({
            color: tokens.Text.Feedback.Alert,
            border: tokens.Border.Feedback.Alert,
            background: tokens.Background.Feedback.Alert,
            icon: <DeleteCircle color={tokens.Icon.Feeback.Alert} />
          });
          break;
        }
        case "warning": {
          setStyling({
            color: tokens.Text.Feedback.Warning,
            border: tokens.Border.Feedback.Warning,
            background: tokens.Background.Feedback.Warning,
            icon: <WarningCircle color={tokens.Icon.Feeback.Warning} />
          });
          break;
        }
        default: {
          setStyling({
            color: tokens.Text.Feedback.Done,
            border: tokens.Border.Feedback.Success,
            background: tokens.Background.Feedback.Success,
            icon: <CheckCircle />
          });
        }
      }
    };
    if (actionStatus?.shouldShow) status = actionStatus?.variant;
    if (status) {
      getStyling(status);
      setShowFeedback(true);
      if ((actionStatus?.shouldShow && actionStatus?.timer) || (status && !actionStatus?.shouldShow))
        setTimeout(
          () => {
            onCloseChange?.();
            setShowFeedback(false);
            actionStatus?.setState && dispatch(actionStatus?.setState);
            dispatch(clearFormState(form));
          },
          actionStatus?.timer ? actionStatus?.timer : FEEDBACK_TIMER
        );
    }
  }, [dispatch, status, form, actionStatus?.shouldShow]);

  return (
    <>
      {showFeedback ? (
        <Snackbar
          ContentProps={{
            sx: {
              color: styling?.color,
              border: `1px solid ${styling?.border}`,
              width: isMobileScreen ? "418px" : "960px",
              paddingY: isMobileScreen ? "14px" : "24px",
              paddingX: "24px",
              background: styling?.background,
              borderRadius: "8px",
              boxShadow: tokens.BoxShadow,
              marginTop: "20px",
              ".MuiSnackbarContent-message": {
                width: "100%",
                padding: "0"
              }
            }
          }}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={true}
          message={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px"
              }}
            >
              {styling?.icon}
              <Typography variant="body1">
                {status === responseStatusTypes.success || actionStatus ? (
                  <Box sx={{ cursor: "pointer" }}>
                    {message}
                    {linkLabel ? (
                      clickHandler ? (
                        <Link sx={{ ml: "5px" }} color={styling?.color} onClick={clickHandler}>
                          {linkLabel}
                        </Link>
                      ) : (
                        <Link sx={{ ml: "5px" }} color={styling?.color} href={href}>
                          {linkLabel}
                        </Link>
                      )
                    ) : (
                      <></>
                    )}
                  </Box>
                ) : Array.isArray(details) ? (
                  details?.map((error: any) => {
                    return <Box key={error.title}>{error.detail}</Box>;
                  })
                ) : (
                  details
                )}
              </Typography>
              <IconButton
                sx={{ ml: "auto", display: isCrossHidden ? "none" : "inherit" }}
                onClick={(e: any) => {
                  onCloseChange?.();
                  setShowFeedback(false);
                  actionStatus?.setState && dispatch(actionStatus?.setState);
                  e.stopPropagation();
                }}
              >
                <CloseIcon color={styling?.color} />
              </IconButton>
            </Box>
          }
        />
      ) : (
        <></>
      )}
    </>
  );
}

export default Feedback;
